import React from 'react';
import { WidgetCard } from '../atoms/WidgetCard';
import { NoteWidgetEditModeHeaderBar } from '../atoms/EditModeChartWidgetHeaderBar';
import { WidgetBody } from '../atoms/WidgetBody';
import { NoDataDoqy } from '../atoms/NoDataDoqy';
import { FlexBox } from '@ardoq/layout';
import { Header1 } from '@ardoq/typography';
import { MarkdownViewer } from '@ardoq/markdown';
import styled from 'styled-components';
import { WidgetErrorBody } from '../atoms/WidgetErrorBody';
import { FullscreenButton } from '../atoms/FullscreenButton';
import { s8 } from '@ardoq/design-tokens';
import { NoteWidgetProps } from '../renderTypes';
import { DashboardModes } from '../../types';
import { type WithI18nLocale, t } from '@ardoq/i18n';

const EmptyPlaceholder = ({ i18nLocale }: WithI18nLocale) => (
  <Header1 color={'textSubtle'}>
    {t(i18nLocale, 'dashboard:fullscreenWidget.noteWidgetName', 'Note')}
  </Header1>
);

const StyledMarkdownViewer = styled(MarkdownViewer)`
  overflow-wrap: anywhere;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-right: ${s8};
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.4); /* scrollbar color */
    border-radius: 4px;
  }
`;

export const NoteWidget = (props: NoteWidgetProps & WithI18nLocale) => {
  const { isInEditMode, _id, layout, content, hasError, i18nLocale } = props;

  return (
    <WidgetCard
      widgetId={_id}
      dashboardMode={isInEditMode ? DashboardModes.EDIT : DashboardModes.VIEW}
      layout={layout}
      isSelected={isInEditMode && props.isSelected}
      hasError={hasError}
    >
      {isInEditMode && !props.isInLinkDashboardFiltersMode && (
        <NoteWidgetEditModeHeaderBar
          commands={props.commands}
          editButtonTooltipText={props.editButtonTooltipText}
          isSaving={props.isSaving}
          i18nLocale={i18nLocale}
        />
      )}
      <WidgetBody hasHeader={isInEditMode}>
        {!hasError ? (
          <>
            <FlexBox
              width={'full'}
              height={'full'}
              align={'center'}
              justify={'center'}
              padding={'medium'}
              flexDirection={'column'}
              gap={'small'}
              position={'relative'}
            >
              {content ? (
                <StyledMarkdownViewer
                  content={content ?? ''}
                  sanitationLevel="permissive"
                />
              ) : (
                <EmptyPlaceholder i18nLocale={i18nLocale} />
              )}
            </FlexBox>
            {!isInEditMode && props.expandWidget && (
              <FullscreenButton
                onClick={props.expandWidget}
                i18nLocale={i18nLocale}
              />
            )}
          </>
        ) : hasError && isInEditMode ? (
          <WidgetErrorBody
            errors={props.viewValidationErrors}
            i18nLocale={i18nLocale}
          />
        ) : (
          <NoDataDoqy
            dashboardMode={
              isInEditMode ? DashboardModes.EDIT : DashboardModes.VIEW
            }
            i18nLocale={i18nLocale}
          />
        )}
      </WidgetBody>
    </WidgetCard>
  );
};
